import { makeAutoObservable, runInAction } from 'mobx'
import { RequestCancelAxios } from 'shared/api/lib'
import { logger } from 'shared/lib'
import { ConversationsApi } from 'entities/Conversation'
import {
  type IResponseConversationsOrder,
  type IResponseConversationsOrderKey,
} from 'entities/Conversation/api/types'

type ISubscription = (order: string) => void

type IOrdersStoreConfig = {
  onChange?: (filter: IResponseConversationsOrder) => void
}

export class OrdersStore {
  private _subscriptions = new Set<ISubscription>()
  private _cancelRequest = new RequestCancelAxios()
  private _loading = false
  private _ordersMap: Map<IResponseConversationsOrderKey, IResponseConversationsOrder> = new Map()
  private _order: IResponseConversationsOrder = {
    key: 'newest',
    label: 'Newest',
  }

  constructor(private _config?: IOrdersStoreConfig) {
    makeAutoObservable(this)
  }

  get loading() {
    return this._loading
  }

  get order() {
    return this._order
  }

  reset = () => {
    this._loading = false
    this._cancelRequest.reset()
  }

  loadOrders = async () => {
    if (this._ordersMap.size) return

    try {
      runInAction(() => {
        this._loading = true
      })

      this._cancelRequest.init()

      const response = await ConversationsApi.getOrdersList({
        cancelToken: this._cancelRequest.token,
      })
      const orders = response.data

      this.addItems(orders)
    } catch (e) {
      logger.error(e)
    } finally {
      runInAction(() => {
        this._loading = false
      })
    }
  }

  setItem = (id: IResponseConversationsOrderKey, silent = false) => {
    const order = this.getItem(id)

    if (order) {
      this._order = order
      this._config?.onChange?.(order)

      if (!silent) this._subscriptions.forEach((subscription) => subscription(order.key))
    }
  }

  addItems = (items: IResponseConversationsOrder[]) => {
    items.forEach((item) => {
      this._ordersMap.set(item.key, item)
    })
  }

  getItem = (key: IResponseConversationsOrderKey) => {
    return this._ordersMap.get(key)
  }

  subscribeOrderChange(subscription: ISubscription) {
    this._subscriptions.add(subscription)
  }

  unsubscribeOrderChange(subscription: ISubscription) {
    this._subscriptions.delete(subscription)
  }

  get orders() {
    return Array.from(this._ordersMap.values())
  }
}
